import { moment } from "../date";
import type { AbsenceGeneralFormSchema } from "../schemas/absence";
import type { AbsenceListItem } from "../types/absences";
import type { Tables } from "../types/database.types";
import { getEmploymentForDate } from "./get-employment-for-date";
import { getEmploymentHolidays } from "./get-employment-holidays";

interface GetAbsenceDaysProps {
	absence: AbsenceListItem;
	employments: Array<Tables<"employments">>;
	organizationSettings: AbsenceGeneralFormSchema;
}

/**
 * Calculate the number of vacation days consumed by a single absence,
 * applying the same deduction rules as the overall balance calculation.
 *
 * Returns 0 immediately when the absence type does not deduct from
 * the balance. Otherwise iterates every calendar day in the absence
 * range and skips public holidays, days outside all employment spans,
 * and weekdays not in the covering employment's scheduled workdays.
 * Half-day deductions (0.5) are applied for `halfDayStart`,
 * `halfDayEnd`, and recurring org-level `halfVacationDays` entries.
 *
 * @param props.absence - The joined absence + absenceType record to evaluate.
 * @param props.employments - All employments for the user, used to
 *   determine workdays, region, and active date ranges.
 * @param props.organizationSettings - Organisation absence settings; the
 *   `halfVacationDays` list is used to apply 0.5-day deductions on
 *   recurring month/day pairs.
 * @returns Total vacation days consumed by this absence (may be fractional).
 */
export function getAbsenceDays({
	absence,
	employments,
	organizationSettings,
}: GetAbsenceDaysProps): number {
	if (!absence.deductsFromBalance) return 0;

	const holidays = employments.flatMap(getEmploymentHolidays);
	const dates = moment().rangeDatesDb(absence.startDate, absence.endDate);

	let total = 0;

	for (const date of dates) {
		const dateMoment = moment(date);
		if (holidays.includes(date)) continue;
		const employment = getEmploymentForDate(employments, date);
		if (!employment) continue;
		if (!employment.workdays.includes(dateMoment.weekday())) continue;

		const month = Number(dateMoment.format("MM"));
		const day = Number(dateMoment.format("DD"));
		let deduction = 1;

		const isFirstDay = date === absence.startDate;
		const isLastDay = date === absence.endDate;
		if ((isFirstDay && absence.halfDayStart) || (isLastDay && absence.halfDayEnd)) {
			deduction = 0.5;
		}

		for (const hvd of organizationSettings.halfVacationDays) {
			if (hvd.month === month && hvd.day === day) {
				deduction = 0.5;
				break;
			}
		}

		total += deduction;
	}

	return total;
}
